// Carátula imprimible del Expediente Administrativo (tamaño OFICIO = Legal,
// 216×356mm). Diseño fijo que se rellena con los datos del expediente.
// - Imprimir: vía iframe con @page size oficio (lo maneja el navegador).
// - Descargar PDF: backend Puppeteer (/api/pdf/from-html) con format 'Legal'.
import React, { useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { ESCUDO_BASE64 } from '../../utils/escudoFiscalia';
import { descargarHtmlComoPdf } from '../../utils/descargarPdf';

const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Construye el HTML completo de la carátula (oficio). Self-contained (escudo en
// base64) para que sirva igual en impresión y en el PDF de Puppeteer.
export function buildCaratulaHtml(data = {}) {
    const nro = esc(data.nro_expediente || data.expediente_adm || '—');
    const iniciador = esc((data.iniciador || '—').toUpperCase());
    const extracto = esc((data.extracto || '—').toUpperCase());
    const reparticion = esc(data.reparticion_origen || data.reparticion || '');
    const fi = String(data.fecha_inicio || '');
    const anio = (fi.match(/^(\d{4})/) || [])[1] || String(new Date().getFullYear());

    return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"><title>Carátula ${nro}</title>
<style>
  @page { size: 216mm 356mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { font-family: 'Times New Roman', Times, serif; color: #161616; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 216mm; min-height: 356mm; padding: 12mm; }
  .frame { border: 2.5px solid #161616; min-height: 332mm; padding: 12mm 14mm; display: flex; flex-direction: column; }
  .anio { text-align: center; font-size: 14pt; font-weight: bold; letter-spacing: 2px; margin-bottom: 16mm; }
  .gob { text-align: center; font-size: 20pt; font-weight: bold; letter-spacing: 1.5px; }
  .escudo { text-align: center; margin: 5mm 0 1mm; }
  .escudo img { width: 50mm; height: auto; }
  .fiscalia { text-align: center; font-size: 20pt; font-weight: bold; }
  .mesa { text-align: center; font-size: 17pt; font-weight: bold; margin: 9mm 0 4mm; letter-spacing: 0.5px; }
  .mesa span { border-bottom: 2px solid #161616; padding: 0 6mm 1mm; }
  .sigif { text-align: center; font-size: 15pt; font-weight: bold; letter-spacing: 5px; margin-bottom: 12mm; }
  .datos { border: 1.5px solid #161616; padding: 7mm 8mm; font-size: 13.5pt; line-height: 1.5; }
  .row { margin-bottom: 6mm; }
  .row:last-child { margin-bottom: 0; }
  .lbl { font-weight: bold; text-transform: uppercase; font-size: 11pt; letter-spacing: 0.8px; color: #333; margin-right: 4px; }
  .val { font-weight: bold; }
  .extracto .val { font-weight: normal; text-align: justify; }
  .spacer { flex: 1; min-height: 14mm; }
</style></head>
<body>
  <div class="page"><div class="frame">
    <div class="anio">AÑO: ${anio}</div>
    <div class="gob">GOBIERNO DE SAN JUAN</div>
    <div class="escudo"><img src="${ESCUDO_BASE64}" alt="Escudo"></div>
    <div class="fiscalia">Fiscalía de Estado</div>
    <div class="mesa"><span>Mesa de Entradas</span></div>
    <div class="sigif">SIGIF</div>
    <div class="datos">
      <div class="row"><span class="lbl">Expte. N°:</span> <span class="val">${nro}</span></div>
      ${reparticion ? `<div class="row"><span class="lbl">Repartición:</span> <span class="val">${reparticion}</span></div>` : ''}
      <div class="row"><span class="lbl">Iniciador:</span> <span class="val">${iniciador}</span></div>
      <div class="row extracto"><span class="lbl">Extracto:</span> <span class="val">${extracto}</span></div>
    </div>
    <div class="spacer"></div>
  </div></div>
</body></html>`;
}

function imprimirCaratula(html) {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open(); doc.write(html); doc.close();
    // Esperar a que cargue el escudo antes de imprimir.
    const go = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* */ } }, 1500);
    };
    if (iframe.contentWindow) {
        iframe.contentWindow.onload = go;
        // fallback por si onload no dispara
        setTimeout(go, 700);
    }
}

export default function CaratulaAdminDialog({ open, onClose, data }) {
    const html = useMemo(() => buildCaratulaHtml(data || {}), [data]);
    const [generando, setGenerando] = React.useState(false);

    const descargar = async () => {
        setGenerando(true);
        try {
            const nro = (data?.nro_expediente || data?.expediente_adm || 'expediente').replace(/[^a-zA-Z0-9_-]/g, '_');
            await descargarHtmlComoPdf(html, {
                filename: `Caratula_${nro}`,
                format: 'Legal',
                rawHtml: true,
                margin: { top: '0mm', right: '0mm', bottom: '0mm', left: '0mm' },
            });
        } catch (e) {
            // eslint-disable-next-line no-alert
            console.error('Error generando PDF de carátula:', e);
        } finally {
            setGenerando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ bgcolor: '#c62828', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
                Carátula del Expediente
                <Typography variant="caption" sx={{ opacity: 0.85, ml: 1 }}>(tamaño oficio)</Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: '#525659' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, maxHeight: '70vh', overflow: 'auto' }}>
                    <iframe
                        title="Vista previa carátula"
                        srcDoc={html}
                        style={{ width: '216mm', height: '356mm', border: 'none', background: '#fff', boxShadow: '0 0 14px rgba(0,0,0,0.5)' }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#f5f5f5' }}>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={descargar} disabled={generando} variant="outlined" startIcon={<PictureAsPdfIcon />}
                    sx={{ textTransform: 'none', borderColor: '#c62828', color: '#c62828' }}>
                    {generando ? 'Generando…' : 'Descargar PDF'}
                </Button>
                <Button onClick={() => imprimirCaratula(html)} variant="contained" startIcon={<PrintIcon />}
                    sx={{ textTransform: 'none', bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
                    Imprimir
                </Button>
            </DialogActions>
        </Dialog>
    );
}
